/**
 * API client for making authenticated requests
 */

import { ensureFreshAccessToken, refreshAccessToken } from '@/contexts/auth/accessToken';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Make an authenticated API request
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await ensureFreshAccessToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  const url = endpoint.startsWith('http') 
    ? endpoint 
    : `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  const send = (bearer: string) => fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      Authorization: `Bearer ${bearer}`,
    },
  });

  let response = await send(token);

  // The access token can lapse between the freshness check and the server
  // (clock skew, a sleeping tab). Refresh once and retry before giving up.
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed.status === 'ok') {
      response = await send(refreshed.data.token);
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: 'Request failed',
      message: response.statusText
    }));

    // Still 401 after a refresh: the session is over (logged out, password
    // changed, revoked). Back to the login screen.
    if (response.status === 401) {
      localStorage.removeItem('littlebooker_auth');
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }

    throw new Error(errorData.message || errorData.error || 'Request failed');
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return {} as T;
  }

  return response.json();
}

/**
 * Make a GET request
 */
export async function apiGet<T = any>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET' });
}

/**
 * Make a POST request
 */
export async function apiPost<T = any>(
  endpoint: string,
  data?: any
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * Make a PUT request
 */
export async function apiPut<T = any>(
  endpoint: string,
  data?: any
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * Make a PATCH request
 */
export async function apiPatch<T = any>(
  endpoint: string,
  data?: any
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * Make a DELETE request
 */
export async function apiDelete<T = any>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Public (unauthenticated) request helpers — for the anonymous invite / schedule
// / timetable flows (RLS Phase 3.2). These hit the /api/v1/public/* endpoints,
// which authenticate by the flow token in the query string, NOT a session JWT —
// so unlike apiRequest they send no Authorization header and never force a
// logout. Callers pass a `token` and any body; the server scopes everything.
// ---------------------------------------------------------------------------

export class PublicApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'PublicApiError';
  }
}

export async function publicApiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  const response = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: 'Request failed', message: response.statusText }));
    throw new PublicApiError(
      response.status,
      errorData.message || errorData.error || 'Request failed',
    );
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return {} as T;
  }
  return response.json();
}

/** Build a /api/v1/public/... path with a token query param (+ extra params). */
function publicPath(
  path: string,
  params: Record<string, string | undefined> = {},
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, value);
  }
  const qs = search.toString();
  return `/api/v1/public${path}${qs ? `?${qs}` : ''}`;
}

export async function apiGetPublic<T = any>(
  path: string,
  params?: Record<string, string | undefined>,
): Promise<T> {
  return publicApiRequest<T>(publicPath(path, params), { method: 'GET' });
}

export async function apiPatchPublic<T = any>(
  path: string,
  params: Record<string, string | undefined>,
  data?: unknown,
): Promise<T> {
  return publicApiRequest<T>(publicPath(path, params), {
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
}

export async function apiPostPublic<T = any>(
  path: string,
  params: Record<string, string | undefined>,
  data?: unknown,
): Promise<T> {
  return publicApiRequest<T>(publicPath(path, params), {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}
